import Button from "../../../Buttons/Button";
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { Column } from "../../../../style";
import { secondary } from "../../../../theme";
import { useApi } from "../../../../hooks/api";
import { useGlobal } from "../../../../hooks/global";
import { ModalLoading } from "../../../Loadings/ModalLoading";
import { accessTypesOptions } from "../../../../utils/options";
import { IGroupProps } from "../../../../interfaces";
import { ContainerButtons, ContainerForm, Form } from "./style";
import { IGenericFormRefProps } from "../../../GenericForm";
import { useWallet } from "../../../../hooks/wallet";
import { BlockForm } from "../../../BlockForm";
import { FaBed, FaDoorOpen, FaWifi } from "react-icons/fa";
import { TbFileDescription } from "react-icons/tb";

interface IFormPersonModalProps {
  onCancel: Function;
  onSave: Function;
  defaultData?: any | null;
  areasGroupsOptions: { label: string; value: string }[];
  smartlocksOptions: { label: string; value: string }[];
}

export const FormAccommodation: React.FC<IFormPersonModalProps> = (props) => {
  const { onSave, defaultData, areasGroupsOptions, smartlocksOptions } = props;

  const { theme, notify } = useGlobal();
  const { api, consultsServices } = useApi();
  const { subscription } = useWallet();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setError,
    setValue,
    trigger,
  } = useForm({
    defaultValues: defaultData?.id ? defaultData : ({} as any),
    mode: "all",
  });

  const _form = watch();

  const form_ref = useRef<IGenericFormRefProps>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [loadingCreate, setLoadingCreate] = useState(false);

  const onSubmit = useCallback(async () => {
    setLoadingCreate(true);

    try {
      const form = form_ref.current?.getForm();
      form.passwords = [form.password_1, form.password_2, form.password_3, form.password_4, form.password_5].filter((password: string) => !!password?.trim());
      form.areas_groups_ids = form.areas_groups_ids?.map(({ id }: any) => id);
      form.smartlock_alias = form?.smartlock_alias ? String(form?.smartlock_alias) : null;

      if (defaultData?.id) await api.put(`/accommodations/${defaultData?.id}`, form);
      else await api.post("/accommodations", form);

      notify(`Acomodação ${defaultData?.id ? "atualizada" : "criada"} com sucesso!`, "success");

      onSave();
    } catch (err: any) {
      console.error(err);
      const error = err.response ? err.response?.data : "SERVER ERROR";
      if (error.field)
        setError(error.field, { type: "manual", message: error.detail });
      else notify("ERRO INTERNO DO SISTEMA", "error");
    }

    setLoadingCreate(false);
  }, [form_ref, _form, api]);

  const handleStopPropagation = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e?.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit, form_ref, _form, api]
  );

  const isHaveMagikeyPermission = subscription?.plans?.permissions.includes("ELECTRONIC_LOCKS_INTEGRATION");
  
  const groups: IGroupProps[] = [
    {
      name: "",
      label: "Dados da acomodação",
      description: "Informações da acomodação importadas do PMS",
      icon: FaBed,
      fields: [
        [
          {
            name: "stays_id",
            label: "Acomodação",
            type: "select-single-no-creatable",
            isClearable: true,
            required: true,
            getIsDisabled: () => !!defaultData?.id,
            searchOptions: consultsServices.stays_id,
          },
        ],
        [
          {
            name: "accommodations_groups_links",
            label: "Grupos de Acomodação",
            type: "select-fixed-multi",
            options: defaultData.accommodations_groups_links,
            getIsDisabled: () => true,
          }
        ]
      ],
    },
    {
      name: "",
      label: "Formas de acesso",
      description: "Definições dos dados de acesso à acomodação",
      icon: FaDoorOpen,
      fields: [
        [
          {
            name: "access_type",
            label: "Tipo de Acesso",
            type: "select-fixed",
            required: true,
            isClearable: true,
            options: accessTypesOptions,
          },
        ],
        [
          {
            name: "areas_groups_ids",
            label: "Áreas de Acesso Magikey",
            type: "select-fixed-multi",
            icon: "🏠",
            options: areasGroupsOptions,
            canSee: () => isHaveMagikeyPermission ?? false,
          },
          {
            name: "smartlock_alias",
            label: "Fechadura Magikey",
            type: "select-fixed",
            options: smartlocksOptions,
            canSee: (data: any) => data?.access_type?.value === "on" && (isHaveMagikeyPermission ?? false),
            isClearable: true,
          },
        ],
        [
          {
            name: "password_1",
            label: "1ª Senha",
            type: "input",
            required: true,
            isPassword: true,
            canSee: (data: any) => data?.access_type?.value === "off",
          },
          {
            name: "password_2",
            label: "2ª Senha",
            type: "input",
            required: false,
            isPassword: true,
            canSee: (data: any) => data?.access_type?.value === "off",
          },
          {
            name: "password_3",
            label: "3ª Senha",
            type: "input",
            required: false,
            isPassword: true,
            canSee: (data: any) => data?.access_type?.value === "off",
          },
          {
            name: "password_4",
            label: "4ª Senha",
            type: "input",
            required: false,
            isPassword: true,
            canSee: (data: any) => data?.access_type?.value === "off",
          },
          {
            name: "password_5",
            label: "5ª Senha",
            type: "input",
            required: false,
            isPassword: true,
            canSee: (data: any) => data?.access_type?.value === "off",
          }
        ],
      ]
    },
    {
      name: "",
      label: "Informações do wi-fi",
      description: "Informe os dados de acesso a rede wi-fi",
      icon: FaWifi,
      fields: [
        [
          {
            name: "wifi_name",
            label: "Nome da Rede Wi-Fi",
            type: "input",
          },
          {
            name: "wifi_password",
            label: "Senha da Rede Wi-Fi",
            type: "input",
            isPassword: true,
          },
        ]
      ]
    },
    {
      name: "",
      label: "MAIS INFORMAÇÕES",
      description: "Informe mais detalhes de sua acomodação",
      icon: TbFileDescription,
      fields: [
        [
          {
            name: "more_information",
            label: "Mais Informações",
            type: "textarea",
            maxChars: 1000,
          },
        ]
      ]
    },
  ];

  useEffect(() => {
    const inputs = document.getElementsByTagName("input");
    for (let i = 0; i < inputs.length; i++)
      inputs[i].setAttribute("autocomplete", "new-password");
  }, [_form.access_type]);

  return (
    <>
      <Column style={{ flex: 1, overflow: "auto", padding: 20 }}>
        <ContainerForm open>
          <Form onSubmit={handleStopPropagation}>
            <BlockForm
              ref={form_ref}
              groups={groups}
              _form={_form}
              control={control}
              trigger={trigger}
              errors={errors}
              setValue={setValue}
              register={register}
            />
            <button
              className="display-none"
              type="submit"
              ref={submitButtonRef}
            />
          </Form>
        </ContainerForm>
      </Column>

      <ContainerButtons theme={theme}>
        <Button
          onClick={() => submitButtonRef.current?.click()}
          background={secondary[theme]}
          color={"#FFF"}
          style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
          children={"Salvar"}
        />
      </ContainerButtons>

      <ModalLoading loading={loadingCreate} theme={theme} />
    </>
  );
};
